import { Box, CircularProgress, Grow, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import LiveHelpIcon from "../../assets/icon/live-help";
import IconButton from "../../components/icon-button/icon-button";
import { useSelector } from "react-redux";
import { getMode } from "../../redux/layoutSlice";
import CloseIcon from "../../assets/icon/close";
import ControlledInput from "../../components/input/controlled-input";
import Fab from "../../components/fab/fab";
import SendIcon from "../../assets/icon/send";
import { getUid } from "../../redux/authSlice";
import { useFirebaseRealtimeQuery } from "../../hooks/useFirebaseRealtimeQuery";
import type { SupportChatType, SupportChatMessageType } from "../../types/support";
import { orderByChild, equalTo } from "firebase/database";
import { post } from "../../api/setting/actions";
import ChatIcon from "../../assets/icon/chat";
import { PeopleAltOutlined } from "@mui/icons-material";
import { useResponsive } from "../../hooks/use-media-query";

const LiveSupport: React.FC = () => {
  const [isLiveChatOpen, setIsLiveChatOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [sendMessageLoading, setSendMessageLoading] = useState<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);
  const prevOpenRef = useRef(false);
  const hasOpenedWithMessagesRef = useRef(false);
  const hasAnimatedOpenRef = useRef(false);

  const mode = useSelector(getMode);
  const uid = useSelector(getUid);

  const { isMobile } = useResponsive();

  const supportChatsPath = uid ? `supportChats/${uid}` : null;

  const { data: currentOpenChat } = useFirebaseRealtimeQuery<SupportChatType | null>({
    queryKey: ["currentOpenChat", uid],
    path: supportChatsPath,
    enabled: !!uid,
    defaultValue: null,
    filters: [orderByChild("status"), equalTo("open")],
    limitToLast: 1,
    transform: (val) => {
      if (!val || typeof val !== "object") return null;
      const entries = Object.entries(val);
      // Query returns 0 or 1 open chat (newest by key); take the last entry
      const last = entries[entries.length - 1];
      if (!last) return null;
      const [id, data] = last;
      return { id, ...(data as Omit<SupportChatType, "id">) } as SupportChatType;
    },
  });

  const openChatId = currentOpenChat?.id ?? null;
  const messagesPath = uid && openChatId ? `supportChats/${uid}/${openChatId}/messages` : null;

  const { data: messages = [], isLoading: isMessagesLoading } = useFirebaseRealtimeQuery<
    SupportChatMessageType[]
  >({
    queryKey: ["supportChatMessages", uid, openChatId],
    path: messagesPath,
    enabled: isLiveChatOpen && !!openChatId,
    defaultValue: [],
    transform: (val) => {
      if (!val || typeof val !== "object") return [];
      return Object.entries(val).map(([pushId, msg]) => ({
        ...(typeof msg === "object" && msg !== null ? msg : {}),
        pushId,
      })) as SupportChatMessageType[];
    },
  });

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.currentTarget.value);
  }, []);

  const handleMarkAsRead = useCallback(() => {
    const data = {
      type: "supportChats",
      id: currentOpenChat?.id,
      dealerUid: uid,
    };
    post(data, `salesagent-dealer-support/mark-read`);
  }, [currentOpenChat?.id, uid]);

  const handleSend = useCallback(() => {
    if (sendMessageLoading || text.trim() === "") return;
    setSendMessageLoading(true);
    setText("");
    const data = {
      message: text.trimStart(),
      forceNewThread: !currentOpenChat ? true : false,
    };
    post(data, `salesagent-dealer-support/send-message`)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSendMessageLoading(false);
      });
  }, [currentOpenChat, sendMessageLoading, text]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter") {
        if (e.shiftKey || sendMessageLoading) {
          // If Shift + Enter is pressed, allow multi-line input
          return;
        } else {
          handleSend();
          return;
        }
      }
    },
    [handleSend, sendMessageLoading]
  );

  useEffect(() => {
    if (
      isLiveChatOpen &&
      currentOpenChat &&
      currentOpenChat.unreadCount &&
      currentOpenChat.unreadCount > 0
    ) {
      handleMarkAsRead();
    }
  }, [currentOpenChat, handleMarkAsRead, isLiveChatOpen]);

  // On reopen: set scroll to bottom before paint so the list appears already at bottom (no visible scroll)
  useLayoutEffect(() => {
    if (
      !isLiveChatOpen ||
      !hasOpenedWithMessagesRef.current ||
      !messagesContainerRef.current ||
      messages.length === 0
    )
      return;
    const el = messagesContainerRef.current;
    el.scrollTop = el.scrollHeight;
  }, [isLiveChatOpen, messages.length]);

  // Scroll messages to bottom on first open (smooth) and when new messages are added
  useEffect(() => {
    if (!isLiveChatOpen) {
      prevOpenRef.current = false;
      return;
    }
    if (!messagesContainerRef.current || messages.length === 0) return;

    const justOpened = !prevOpenRef.current;
    prevOpenRef.current = true;

    const prev = prevMessagesLengthRef.current;
    prevMessagesLengthRef.current = messages.length;

    const isReopen = justOpened && hasOpenedWithMessagesRef.current;
    const isNewMessage = !justOpened && messages.length > prev;
    if (messages.length > 0) hasOpenedWithMessagesRef.current = true;

    const behavior: ScrollBehavior = isReopen
      ? "auto"
      : isNewMessage
      ? "smooth"
      : justOpened
      ? "smooth"
      : "auto";
    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior,
    });
  }, [messages, isLiveChatOpen]);

  // Mark that we've opened at least once so Grow only animates on first open
  useEffect(() => {
    if (isLiveChatOpen) hasAnimatedOpenRef.current = true;
  }, [isLiveChatOpen]);

  const chatTransition = hasAnimatedOpenRef.current
    ? { enter: 0, exit: 220 }
    : { enter: 280, exit: 220 };

  return (
    <Box sx={{ position: "fixed", bottom: "80px", right: "24px", zIndex: 1000 }}>
      <Grow
        in={isLiveChatOpen}
        timeout={chatTransition}
        style={{ transformOrigin: "bottom right" }}
        unmountOnExit={false}
      >
        <Box
          sx={{
            position: isMobile ? "fixed" : "absolute",
            top: isMobile ? "" : "-600px",
            left: isMobile ? "0" : "-400px",
            right: isMobile ? "0" : "",
            height: isMobile ? "70dvh" : "600px",
            margin: isMobile ? "0 auto" : "",
            bottom: isMobile ? "130px" : "",
            width: isMobile ? "90%" : "400px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #8080802e",
            borderRadius: "12px",
            borderBottomRightRadius: "0",
            backgroundColor: (theme) => theme.palette.background.paper,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2B7FFF",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              padding: "12px",
              gap: "12px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
              <ChatIcon sx={{ width: "16px", height: "16px" }} />
              <Typography sx={{ color: "#fff", fontSize: "14px", fontWeight: "500" }}>
                Drivee Live Support
              </Typography>
            </Box>
            <IconButton
              icon={<CloseIcon sx={{ width: "16px", height: "16px", fill: "#fff" }} />}
              onClick={() => setIsLiveChatOpen(false)}
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            />
          </Box>
          <Box sx={{ padding: "12px", borderBottom: "1px solid #8080802e" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
              <PeopleAltOutlined sx={{ width: "16px", height: "16px" }} />
              <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>Support Team</Typography>
              <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "4px" }}>
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    backgroundColor: "#1ed71e",
                    borderRadius: "50%",
                  }}
                ></Box>
                <Typography sx={{ fontSize: "10px", fontWeight: "400", color: "#1ed71e" }}>
                  Online
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: "11px", fontWeight: "400" }}>
              Chat with us anytime 24/7
            </Typography>
          </Box>
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflow: "auto",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minHeight: 0,
              maxHeight: "calc(600px - 120px - 67px)",
              scrollBehavior: "smooth",
            }}
          >
            {!isMessagesLoading &&
              (messages.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  No messages yet. Start the conversation.
                </Typography>
              ) : (
                messages
                  .slice()
                  .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
                  .map((msg) => {
                    const isDealer = msg.senderType === "dealer";
                    return (
                      <Box
                        key={msg.pushId}
                        sx={{
                          alignSelf: isDealer ? "flex-end" : "flex-start",
                          maxWidth: "85%",
                          padding: "8px 12px",
                          borderRadius: "12px",
                          backgroundColor: isDealer
                            ? "#2B7FFF"
                            : (theme) => theme.palette.action.hover,
                          color: isDealer ? "#fff" : "text.primary",
                        }}
                      >
                        {!isDealer && msg.senderName && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              opacity: 0.9,
                              marginBottom: "4px",
                              fontSize: "11px",
                            }}
                          >
                            {msg.senderName}
                            {msg.senderTitle ? ` · ${msg.senderTitle}` : ""}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                          {msg.message || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            opacity: 0.8,
                            marginTop: "4px",
                            fontSize: "0.7rem",
                          }}
                        >
                          {msg.timestamp
                            ? new Date(msg.timestamp).toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </Typography>
                      </Box>
                    );
                  })
              ))}
          </Box>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              width: "100%",
              borderTop: "1px solid #8080802e",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              padding: "12px",
            }}
          >
            <ControlledInput
              placeholder="Write your message here..."
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              sx={{ height: "40px" }}
            />
            <Fab
              icon={
                sendMessageLoading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <SendIcon sx={{ width: "18px", height: "18px" }} />
                )
              }
              onClick={handleSend}
              color="primary"
              variant="extended"
              sx={{
                backgroundColor: "#2B7FFF",
                borderRadius: "8px",
                boxShadow: "none",
                height: "40px",
                width: "44px",
              }}
            />
          </Box>
        </Box>
      </Grow>
      <Tooltip
        title={
          <span>
            Need help? <br /> Live chat with support.
          </span>
        }
        placement="left"
      >
        <span>
          <IconButton
            onClick={() => setIsLiveChatOpen((value) => !value)}
            icon={
              <LiveHelpIcon
                sx={{ width: "20px", height: "20px" }}
                fill={mode === "dark" ? "#000" : "#fff"}
              />
            }
            sx={{
              backgroundColor: mode === "dark" ? "#fff" : "#000",
              "&:hover": { backgroundColor: mode === "dark" ? "#fff" : "#000" },
              width: "48px",
              height: "48px",
            }}
          />
          {currentOpenChat?.unreadCount && currentOpenChat?.unreadCount > 0 ? (
            <Box
              sx={{
                position: "absolute",
                width: "20px",
                height: "20px",
                backgroundColor: "#ff5656",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: "400",
                color: "#fff",
                top: "-4px",
                left: "-2px",
              }}
            >
              {currentOpenChat?.unreadCount}
            </Box>
          ) : null}
        </span>
      </Tooltip>
    </Box>
  );
};

export default LiveSupport;
